/*
 * sunset-delivery-zones.js
 * Sunset Landscape Supply — ZIP-code delivery fee lookup (two-market build).
 *
 * Markets (each with its own dispatch yard and its own rates):
 *   LA — Louisiana Northshore, dispatched from the Slidell yard.
 *   FL — Florida Panhandle,    dispatched from the Pensacola yard.
 *
 * Pricing rule (per market; both markets currently share placeholder rates):
 *   fee = flatFee + max(0, miles - freeMiles) * perMile, rounded to the
 *   nearest whole dollar. The fee is charged PER LOAD (dump-truck trip),
 *   matching cart + product-page logic.
 *
 * Data: 29 ZIPs — 17 in St. Tammany Parish, LA (Slidell yard) and 12 in
 * Escambia County, FL (Pensacola yard). Driving miles are ESTIMATES that
 * must be verified before launch; straight-line miles are placeholders
 * derived as round(driving * 0.8) until real great-circle numbers exist.
 *
 * Public API (window.SunsetDelivery):
 *   .lookup(zip)            -> null | {zip, town, place, county, market,
 *                                      marketLabel, origin, label, miles,
 *                                      drivingMiles, straightMiles, fee,
 *                                      inFlatZone}
 *   .quote(zip, loads)      -> {zip, town, miles, feePerLoad, loads, total,
 *                               inFlatZone, known, market, marketLabel,
 *                               inLouisiana, inServiceArea, label}
 *   .format(zip)            -> short user-facing sentence for the current state
 *   .feeForMiles(miles, market) -> number (market optional, defaults to 'LA')
 *   .getZip() / .setZip(z) / .clearZip()
 *   .isLouisiana(zip)   -> boolean. LEGACY NAME, new meaning: "this ZIP
 *                              is in our service table". Kept so existing
 *                              call sites do not break; prefer the alias
 *                              .isInServiceArea(zip).
 *   .isInServiceArea(zip)   -> boolean (alias of the above)
 *   .normalize(zip)         -> '70458' | null
 *   .MARKETS                -> per-market rate config
 *   .FLAT_FEE (125) .FREE_MILES (30 LA / 20 FL) .PER_MILE (4)
 *                              legacy single-market aliases (= LA rates)
 *   .basis ('driving')
 *   .COOKIE ('sunset_zip')
 *
 * Events (on document):
 *   'sunset:ready'      { detail: { zip, quote } }  fired once, after init
 *   'sunset:zip-change' { detail: { zip, quote } }  fired on every setZip/clearZip,
 *                                                 and once at init if a ZIP is stored
 *
 * Storage: plain cookie `sunset_zip`, 30-day expiry, path=/, SameSite=Lax.
 * (localStorage is deliberately NOT the primary store — see PATCHES.md.)
 *
 * NOTE: the Sunset* identifier / cookie / event names are intentionally
 * unchanged here; a separate global rename pass handles those.
 */
(function (w, d) {
  'use strict';
  if (w.SunsetDelivery) return; // idempotent: safe if the asset is included twice

  /* ------------------------------------------------------------------ *
   * TWO-MARKET CONFIG
   * Each delivery market has its own dispatch yard and its own rates.
   * // TWEAK RATES PER MARKET HERE — placeholder values, confirm before launch
   * ------------------------------------------------------------------ */
  var MARKETS = {
    LA: {
      label: 'Louisiana — Northshore',
      origin: 'Slidell yard',
      flatFee: 125,   // dollars per trip, covers everything inside freeMiles
      freeMiles: 30,  // miles included in the per-trip base
      perMile: 4,     // dollars per mile beyond freeMiles
      capacityYd: 6   // cubic yards per trip (8-ton truck)
    },
    FL: {
      label: 'Florida — Pensacola',
      origin: 'Pensacola yard',
      flatFee: 125,
      freeMiles: 20,
      perMile: 4,
      capacityYd: 3   // cubic yards per trip (4-ton trailer)
    }
  };

  /* ------------------------------------------------------------------ *
   * BASIS SWITCH — change this ONE line to swap the distance basis.
   *   'driving'  = road miles (correct for a dump truck)
   *   'straight' = great-circle miles. Currently a placeholder estimated
   *                as round(driving * 0.8); replace with real numbers
   *                before ever switching the basis.
   * ------------------------------------------------------------------ */
  var BASIS = 'driving';

  /* Legacy single-market constants, kept as fallback aliases so nothing
     that still reads them breaks. They mirror the LA (default) market. */
  var FLAT_FEE = MARKETS.LA.flatFee;
  var FREE_MILES = MARKETS.LA.freeMiles;
  var PER_MILE = MARKETS.LA.perMile;
  var COOKIE = 'sunset_zip';
  var COOKIE_DAYS = 30;

  /* Row layout: [drivingMiles, straightMiles, town, countyOrParish, market, place?]
     The market code doubles as the state abbreviation (LA / FL). */
  var MILES_INDEX = BASIS === 'straight' ? 1 : 0;

  // ESTIMATED driving miles — verify before launch.
  // straightMiles = round(drivingMiles * 0.8) placeholder, same caveat.
  var ZONES = {
/* --- LA market: St. Tammany Parish, dispatched from the Slidell yard --- */
"70458":[3,2,"Slidell","St. Tammany Parish","LA"],
"70459":[3,2,"Slidell","St. Tammany Parish","LA"],
"70460":[5,4,"Slidell","St. Tammany Parish","LA"],
"70461":[6,5,"Slidell","St. Tammany Parish","LA"],
"70452":[10,8,"Pearl River","St. Tammany Parish","LA"],
"70445":[12,10,"Lacombe","St. Tammany Parish","LA"],
"70448":[24,19,"Mandeville","St. Tammany Parish","LA"],
"70471":[26,21,"Mandeville","St. Tammany Parish","LA"],
"70433":[30,24,"Covington","St. Tammany Parish","LA"],
"70434":[31,25,"Covington","St. Tammany Parish","LA"],
"70435":[34,27,"Covington","St. Tammany Parish","LA"],
"70420":[27,22,"Abita Springs","St. Tammany Parish","LA"],
"70447":[33,26,"Madisonville","St. Tammany Parish","LA"],
"70437":[42,34,"Folsom","St. Tammany Parish","LA"],
"70431":[24,19,"Bush","St. Tammany Parish","LA"],
"70463":[30,24,"Sun","St. Tammany Parish","LA"],
"70464":[19,15,"Talisheek","St. Tammany Parish","LA"],
/* --- FL market: Escambia County, dispatched from the Pensacola yard --- */
"32501":[3,2,"Pensacola","Escambia County","FL"],
"32502":[2,2,"Pensacola","Escambia County","FL"],
"32503":[5,4,"Pensacola","Escambia County","FL"],
"32504":[8,6,"Pensacola","Escambia County","FL"],
"32505":[5,4,"Pensacola","Escambia County","FL"],
"32506":[9,7,"Pensacola","Escambia County","FL"],
"32507":[12,10,"Pensacola","Escambia County","FL"],
"32514":[10,8,"Pensacola","Escambia County","FL"],
"32526":[12,10,"Pensacola","Escambia County","FL"], "32561": [12, 10, "Pensacola Beach","Escambia County","FL"],
"32534":[11,9,"Pensacola","Escambia County","FL"],
"32533":[15,12,"Cantonment","Escambia County","FL"],
"32577":[22,18,"Molino","Escambia County","FL"]
};

  /* ---------------------------- helpers ---------------------------- */

  function normalize(zip) {
    if (zip === 0 || zip) {
      var s = String(zip).replace(/[^0-9]/g, '');
      if (s.length >= 5) return s.slice(0, 5);
      if (s.length > 0 && s.length < 5) return null;
    }
    return null;
  }

  /* LEGACY NAME, new meaning. Historically "is this a Louisiana ZIP";
     the store now serves Louisiana + Florida, so this answers "is this ZIP
     in our service table". The name is kept so existing call sites keep
     working — `isInServiceArea` is the preferred alias going forward. */
  function isLouisiana(zip) {
    var z = normalize(zip);
    if (!z) return false;
    return Object.prototype.hasOwnProperty.call(ZONES, z);
  }

  /* Fee for a mileage in a given market. `market` is optional and
     defaults to 'LA' (the legacy single-market behaviour). */
  function feeForMiles(miles, market) {
    var mk = (market && MARKETS[market]) || MARKETS.LA;
    var m = Number(miles);
    if (!isFinite(m) || m < 0) m = 0;
    var over = Math.max(0, m - mk.freeMiles);
    return Math.round(mk.flatFee + over * mk.perMile);
  }

  function money(n) {
    return '$' + Number(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function money0(n) {
    return '$' + Math.round(Number(n)).toLocaleString('en-US');
  }

  /* ----------------------------- lookup ---------------------------- */

  function lookup(zip) {
    var z = normalize(zip);
    if (!z) return null;
    var row = Object.prototype.hasOwnProperty.call(ZONES, z) ? ZONES[z] : null;
    if (!row) return null;
    var miles = row[MILES_INDEX];
    var town = row[2];
    var county = row[3];
    var market = row[4] || 'LA';
    var place = row[5] || '';
    var mk = MARKETS[market] || MARKETS.LA;
    return {
      zip: z,
      town: town,
      place: place,
      county: county,
      market: market,
      marketLabel: mk.label,
      origin: mk.origin,
      label: place && place !== town ? place + ', ' + market : town + ', ' + market,
      miles: miles,
      drivingMiles: row[0],
      straightMiles: row[1],
      fee: feeForMiles(miles, market),
      inFlatZone: miles <= mk.freeMiles
    };
  }

  /* ----------------------------- quote ----------------------------- *
   * `loads` is the number of dump-truck trips. The delivery fee is
   * charged PER LOAD — identical to the existing cart maths
   * (loads * DELIVERY_FEE). The fee comes from the ZIP's own market
   * (LA rates from the Slidell yard, FL rates from the Pensacola yard).
   * Unknown ZIPs fall back to the LA flat fee so nothing renders as
   * NaN, but `known:false` means the UI MUST say the fee is confirmed
   * at checkout instead of presenting the number as final.
   * ------------------------------------------------------------------ */
  function quote(zip, loads) {
    var n = parseInt(loads, 10);
    if (!isFinite(n) || n < 1) n = 1;
    var hit = lookup(zip);
    var z = normalize(zip);
    if (!hit) {
      var inArea = isLouisiana(z); // legacy fn name = "in service table"
      return {
        zip: z,
        town: null,
        label: null,
        miles: null,
        market: null,
        marketLabel: null,
        feePerLoad: FLAT_FEE,
        loads: n,
        total: FLAT_FEE * n,
        inFlatZone: false,
        known: false,
        inLouisiana: inArea, // legacy field name, kept for old consumers
        inServiceArea: inArea
      };
    }
    return {
      zip: hit.zip,
      town: hit.town,
      label: hit.label,
      miles: hit.miles,
      market: hit.market,
      marketLabel: hit.marketLabel,
      feePerLoad: hit.fee,
      loads: n,
      total: hit.fee * n,
      inFlatZone: hit.inFlatZone,
      known: true,
      inLouisiana: true, // legacy field name, kept for old consumers
      inServiceArea: true
    };
  }

  /* ---------------------------- messaging -------------------------- */

  function format(zip) {
    var z = normalize(zip);
    if (!z) return 'Enter your delivery ZIP for an exact delivery price.';
    var q = quote(z, 1);
    if (q.known && q.inFlatZone) {
      return 'Delivering to ' + q.label + ' — ' + money0(q.feePerLoad) + ' flat per load.';
    }
    if (q.known) {
      var origin = (MARKETS[q.market] || MARKETS.LA).origin;
      return 'Delivering to ' + q.label + ' — ' + money0(q.feePerLoad) +
        ' per load (' + q.miles + ' mi from our ' + origin + ').';
    }
    if (!q.inServiceArea) {
      return 'That ZIP looks outside our Louisiana & Florida service areas — text (504) 233-0637 and we will confirm if we can reach you.';
    }
    return 'We deliver to ' + z + ' — your exact delivery price is confirmed at checkout.';
  }

  /* ----------------------------- cookie ---------------------------- */

  function readCookie(name) {
    try {
      var parts = ('; ' + d.cookie).split('; ' + name + '=');
      if (parts.length === 2) return decodeURIComponent(parts.pop().split(';').shift());
    } catch (e) {}
    return null;
  }

  function writeCookie(name, value, days) {
    try {
      var exp = new Date(Date.now() + days * 864e5).toUTCString();
      d.cookie = name + '=' + encodeURIComponent(value) +
        '; expires=' + exp + '; path=/; SameSite=Lax';
    } catch (e) {}
  }

  function eraseCookie(name) {
    try {
      d.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax';
    } catch (e) {}
  }

  function getZip() {
    return normalize(readCookie(COOKIE));
  }

  function emit(name, zip) {
    try {
      d.dispatchEvent(new CustomEvent(name, {
        detail: { zip: zip, quote: zip ? quote(zip, 1) : null }
      }));
    } catch (e) {
      /* IE-era fallback; harmless everywhere else */
      try {
        var ev = d.createEvent('CustomEvent');
        ev.initCustomEvent(name, true, false, { zip: zip, quote: zip ? quote(zip, 1) : null });
        d.dispatchEvent(ev);
      } catch (e2) {}
    }
  }

  function setZip(zip) {
    var z = normalize(zip);
    if (!z) return null;
    if (z === getZip()) { emit('sunset:zip-change', z); return z; } // still re-notify listeners
    writeCookie(COOKIE, z, COOKIE_DAYS);
    /* Mirror into localStorage as a best-effort convenience only.
       The cookie is the source of truth; a storage failure is ignored. */
    try { w.localStorage.setItem(COOKIE, z); } catch (e) {}
    emit('sunset:zip-change', z);
    return z;
  }

  function clearZip() {
    eraseCookie(COOKIE);
    try { w.localStorage.removeItem(COOKIE); } catch (e) {}
    emit('sunset:zip-change', null);
  }

  /* ------------------------------ API ------------------------------ */

  w.SunsetDelivery = {
    lookup: lookup,
    quote: quote,
    format: format,
    feeForMiles: feeForMiles,
    normalize: normalize,
    isLouisiana: isLouisiana, // legacy name — means "in service table"
    isInServiceArea: isLouisiana, // preferred alias, same function
    getZip: getZip,
    setZip: setZip,
    clearZip: clearZip,
    money: money,
    money0: money0,
    MARKETS: MARKETS,
    FLAT_FEE: FLAT_FEE,
    FREE_MILES: FREE_MILES,
    PER_MILE: PER_MILE,
    basis: BASIS,
    COOKIE: COOKIE,
    size: 0,
    zones: ZONES
  };

  try { w.SunsetDelivery.size = Object.keys(ZONES).length; } catch (e) {}

  /* Announce ourselves. Any consumer that rendered before this asset
     finished loading gets a chance to re-render. */
  var saved = getZip();
  emit('sunset:ready', saved);
  if (saved) emit('sunset:zip-change', saved);
})(window, document);
